import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Pencil } from 'lucide-react';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableRow } from '../../ui/table';
import { useToast } from '../../ui/use-toast';
import { cn } from '../../../lib/utils';
import { createSerialQueue } from '../../../lib/serial-queue';
import { roundRefreshOptions, useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { competitionRoundsService } from '../../../services/competition-rounds';
import type { CompetitionParticipant } from '../../../types/competition';
import type { BoulderRoundResult, RoundResult } from '../../../types/competition-round';
import { BoulderActionButtons } from '../BoulderActionButtons';
import { BoulderAttemptMarkers } from '../BoulderAttemptMarkers';
import { BoulderEditDialog } from '../BoulderEditDialog';
import { RefreshFlash } from '../RefreshFlash';
import {
  RefreshGenerationContext,
  boulderCardSignature,
  useRefreshGeneration,
} from '../refresh-flash';
import {
  findResultForParticipant,
  getParticipantName,
  getParticipantSurname,
  getParticipantTeam,
} from '../participantUtils';
import { JudgeDoneDivider } from './JudgeDoneDivider';
import { JudgeSearch, JudgeSortHeader } from './JudgeFiltersControls';
import { arrangeParticipants, type JudgeTableProps } from './judge-blocks';

/**
 * Each participant's result on this block (see findResultForParticipant).
 * A plain object, not component state, so an action handler can read "does
 * this participant already have a result" without waiting for a render.
 */
function buildResultsCache(
  participantsList: CompetitionParticipant[],
  resultsList: RoundResult[]
): Map<string, BoulderRoundResult> {
  const cache = new Map<string, BoulderRoundResult>();
  for (const participant of participantsList) {
    const result = findResultForParticipant(participant, resultsList) as BoulderRoundResult | undefined;
    if (result) cache.set(participant.id, result);
  }
  return cache;
}

/**
 * One block, one class: the athletes who climb it, each with the goes the judge
 * has marked. Tap a row to arm the three buttons on it; a top sends the row to
 * the bottom, and the pencil takes a mark back off.
 */
export function JudgeBoulderTable({
  competitionSlug,
  round,
  roundClimb,
  participants,
  filters,
  onFiltersChange,
  scoringOpen,
}: JudgeTableProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [results, setResults] = useState<RoundResult[]>([]);
  // Nothing is drawn before the first answer: a table sorted without its
  // results would put the finished athletes on top, then move them.
  const [ready, setReady] = useState(false);
  // The one athlete the buttons are armed for. Tapping another row moves them
  // there: only one person can be scored at a time, which is also the one thing
  // the judge should be able to see at a glance.
  const [openParticipantId, setOpenParticipantId] = useState<string | null>(null);
  const [editingParticipantId, setEditingParticipantId] = useState<string | null>(null);

  // Runs each participant's own attempts one after another (see
  // createSerialQueue), and the "does a result exist yet" answer they act on —
  // updated the instant a response comes back, not on the next render, so a
  // burst of taps never sees the same stale "no result" twice.
  const runSerially = useRef(createSerialQueue()).current;
  const resultsCacheRef = useRef<Map<string, BoulderRoundResult>>(new Map());
  const participantsRef = useRef(participants);
  participantsRef.current = participants;

  /**
   * The block's results as the server has them — the other judges' and the
   * athletes' own marks included. Put on screen only if no tap of this judge's
   * went out while it was being read (see SerialQueue#quietSince): an answer
   * from before the tap would take it back off the table, and off the cache the
   * next tap reasons from.
   */
  // Bumped with every answer, so a mark somebody else put on a card lights up.
  const [generation, bumpGeneration] = useRefreshGeneration();

  const refreshResults = useCallback(async () => {
    const mark = runSerially.mark();
    try {
      const fetched = await competitionRoundsService.getRoundClimbResults(competitionSlug, round.id, roundClimb.id);
      if (!runSerially.quietSince(mark)) return;
      setResults(fetched);
      bumpGeneration();
      resultsCacheRef.current = buildResultsCache(participantsRef.current, fetched);
    } catch {
      // Silent refresh failure: the table on screen is still the one that counts.
    } finally {
      setReady(true);
    }
  }, [competitionSlug, round.id, roundClimb.id, runSerially, bumpGeneration]);

  useEffect(() => {
    refreshResults();
  }, [refreshResults]);

  // At the competition's pace, while the round takes marks.
  useAutoRefresh(refreshResults, roundRefreshOptions(round));

  const resultFor = (participant: CompetitionParticipant) =>
    findResultForParticipant(participant, results) as BoulderRoundResult | undefined;

  /** A topped boulder is finished business: that row sinks to the bottom. */
  const arranged = arrangeParticipants(participants, filters, (p) => resultFor(p)?.topped === true);

  /** Folds a fresh result into both the cache actions read and the state the table renders. */
  const rememberResult = (participantId: string, result: BoulderRoundResult) => {
    resultsCacheRef.current.set(participantId, result);
    setResults((prev) => {
      const idx = prev.findIndex((r) => r.id === result.id);
      if (idx === -1) return [...prev, result];
      const next = [...prev];
      next[idx] = result;
      return next;
    });
  };

  const handleBoulderAction = (participantId: string, action: 'zone' | 'top' | 'close') =>
    runSerially(participantId, async () => {
      let result = resultsCacheRef.current.get(participantId);

      try {
        // If no result exists, create one first (with 0 attempts)
        if (!result) {
          result = (await competitionRoundsService.createBoulderResult(competitionSlug, round.id, roundClimb.id, {
            participant_id: participantId,
            attempts_count: 0,
          })) as BoulderRoundResult;
          resultsCacheRef.current.set(participantId, result);
        }

        const attemptType = action === 'close' ? 'attempt' : action;
        const updated = (await competitionRoundsService.addBoulderAttempt(
          competitionSlug,
          round.id,
          roundClimb.id,
          result.id,
          attemptType
        )) as BoulderRoundResult;

        // No toast on the way out: the button that was pressed shows the
        // check itself, and the response already carries the new attempt.
        rememberResult(participantId, updated);
        // A top sinks the row to the bottom, done: it lets go of the selection
        // rather than take the pencil down there with it.
        if (updated.topped) setOpenParticipantId((current) => (current === participantId ? null : current));
      } catch (error) {
        toast({
          title: t('errors.actionFailed', { defaultValue: 'Action failed' }),
          description: error instanceof Error ? error.message : undefined,
          variant: 'destructive',
        });
        // Let the button know, so it drops back to idle instead of claiming
        // the attempt was recorded.
        throw error;
      }
    });

  const handleRemoveAttempt = async (attemptId: string) => {
    if (!editingParticipantId) return;
    const result = resultsCacheRef.current.get(editingParticipantId);
    if (!result) return;

    try {
      const updated = (await competitionRoundsService.removeBoulderAttempt(
        competitionSlug,
        round.id,
        roundClimb.id,
        result.id,
        attemptId
      )) as BoulderRoundResult;
      rememberResult(editingParticipantId, updated);
    } catch (error) {
      toast({
        title: t('errors.actionFailed', { defaultValue: 'Action failed' }),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    }
  };

  const editingParticipant = participants.find((p) => p.id === editingParticipantId);
  const editingResult = editingParticipant ? resultFor(editingParticipant) : undefined;

  if (!ready) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <RefreshGenerationContext.Provider value={generation}>
      <JudgeSearch
        filters={filters}
        onChange={onFiltersChange}
        placeholder={t('judgeBoulder.searchPlaceholder', { defaultValue: 'Search participant...' })}
      />

      {/* One athlete per group of rows: who they are, what they have scored,
          and — for the row that is open — what can be added to it. */}
      <Table>
        <JudgeSortHeader filters={filters} onChange={onFiltersChange} />
        <TableBody>
          {arranged.length === 0 && (
            <TableRow>
              <TableCell colSpan={3} className="py-6 text-center text-muted-foreground">
                {t('judgeBoulder.noParticipants', { defaultValue: 'No participants' })}
              </TableCell>
            </TableRow>
          )}

          {arranged.map((p, index) => {
            const result = resultFor(p);
            const attempts = result?.boulder_attempts || [];
            const topped = result?.topped || false;
            // The row tapped last. A topped row is selected too, so its marks
            // can be put straight — but it shows only the pencil: there is
            // nothing to add to a top. Take the top off and the buttons return.
            const selected = openParticipantId === p.id && scoringOpen;
            const open = selected && !topped;
            const select = () => {
              if (scoringOpen) setOpenParticipantId(p.id);
            };
            // The first of the people done with the block, who sink to the
            // bottom: the line goes above them, when there is anybody above.
            const firstDone = topped && index > 0 && resultFor(arranged[index - 1])?.topped !== true;

            return (
              <Fragment key={p.id}>
                {firstDone && <JudgeDoneDivider />}
                <TableRow
                  className={cn('border-0', topped && 'text-muted-foreground', scoringOpen && 'cursor-pointer')}
                  data-state={selected ? 'selected' : undefined}
                  data-testid="participant-row"
                  onClick={select}
                >
                  <TableCell className="py-2 font-medium">{getParticipantName(p)}</TableCell>
                  <TableCell className="py-2">{getParticipantSurname(p)}</TableCell>
                  <TableCell className="py-2">{getParticipantTeam(p)}</TableCell>
                </TableRow>

                <TableRow
                  className={cn(open && 'border-0', scoringOpen && 'cursor-pointer')}
                  data-state={selected ? 'selected' : undefined}
                  data-testid="attempts-row"
                  onClick={select}
                >
                  <TableCell colSpan={3} className="pb-2 pt-0">
                    <div className="flex items-center gap-2">
                      <RefreshFlash
                        value={boulderCardSignature(attempts, topped, result?.zone || false)}
                        className={cn('flex items-center gap-2', topped && 'opacity-60')}
                      >
                        <BoulderAttemptMarkers attempts={attempts} />
                      </RefreshFlash>

                      {/* Only on the row being worked on: on every row it was a
                          column of pencils to tap by mistake. */}
                      {selected && attempts.length > 0 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          data-testid="edit-attempts-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingParticipantId(p.id);
                          }}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>

                {open && (
                  <TableRow data-state="selected" data-testid="actions-row">
                    <TableCell colSpan={3} className="pb-3 pt-0">
                      <BoulderActionButtons
                        topped={topped}
                        onRecordAttempt={() => handleBoulderAction(p.id, 'close')}
                        onRecordZone={() => handleBoulderAction(p.id, 'zone')}
                        onRecordTop={() => handleBoulderAction(p.id, 'top')}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      <BoulderEditDialog
        open={!!editingParticipantId}
        onOpenChange={(next) => !next && setEditingParticipantId(null)}
        attempts={editingResult?.boulder_attempts || []}
        onRemoveAttempt={handleRemoveAttempt}
      />
    </RefreshGenerationContext.Provider>
  );
}
